import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable, override } from 'mobx';
import { OneToolService } from '../../services/oneToolService';

const REQUIRED_MESSAGE = 'This field is required.';

const form = {
  fields: {
    name: { value: '', error: null, rule: 'required', message: REQUIRED_MESSAGE },
    communicateVia: { value: 'Email', error: null, rule: 'required', message: REQUIRED_MESSAGE },
    address: {
      value: null,
      error: null,
      rule: [{ required_if_true: ['isPreflight', 'hasNewAddress'] }],
      message: REQUIRED_MESSAGE,
    },
  },
  meta: { key: null, editMode: false, isValid: false, error: null },
};

export interface TitleContact {
  contactId?: number;
  fullName: string;
  phone?: string;
  email?: string;
}

export interface TitleCompanyItem {
  titleAgencyId: number | null;
  companyId: number | null;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  streetNumber?: string;
  streetName?: string;
  noMatchGoogleAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  contacts: TitleContact[];
}

const emptyNewCompany = () => ({
  titleAgencyId: null as number | null,
  name: '',
  address: null as any,
  streetNumber: '',
  streetName: '',
  noMatchGoogleAddress: '',
  city: '',
  state: '',
  zipCode: '',
});

interface TitleFormSnapshot {
  titleAgencyId: number | null;
  name: string;
  communicateVia: string;
  selectedContactIds: string;
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  zipCode: string;
  includeBroker: boolean;
}

export class TitleFormStore extends FormStore {
  private globalStore: GlobalStore;
  private oneToolService: OneToolService;

  showTitleDialog = false;
  titleCompanies: TitleCompanyItem[] = [];
  searchText = '';
  selectedCompany: TitleCompanyItem | null = null;
  communicateVia = 'Email';
  loading = false;
  newCompany = emptyNewCompany();
  contacts: TitleContact[] = [];
  selectedContactIds: Set<number> = new Set();
  isEditing = false;
  titleFormSnapshot: TitleFormSnapshot | null = null;
  includeBroker = false;

  constructor(globalStore) {
    super({ ...form }, globalStore);
    this.globalStore = globalStore;
    this.oneToolService = new OneToolService();
    makeObservable(this, {
      showTitleDialog: observable,
      titleCompanies: observable,
      searchText: observable,
      selectedCompany: observable,
      communicateVia: observable,
      loading: observable,
      newCompany: observable,
      contacts: observable,
      selectedContactIds: observable,
      isEditing: observable,
      titleFormSnapshot: observable,
      includeBroker: observable,
      setIncludeBroker: action,
      reset: override,
      openDialog: action,
      closeDialog: action,
      saveTitle: action,
      setSearchText: action,
      selectExistingCompany: action,
      prefillNewName: action,
      changeCompany: action,
      setCommunicateVia: action,
      setNewCompanyField: action,
      setAddressField: action,
      loadTitleSelection: action,
      addContact: action,
      updateContact: action,
      removeContact: action,
      toggleContact: action,
      takeTitleSnapshot: action,
      hasChanged: computed,
      fetchTitleCompanies: flow,
    });
  }

  reset() {
    super.reset();
    this.showTitleDialog = false;
    this.titleCompanies = [];
    this.searchText = '';
    this.selectedCompany = null;
    this.communicateVia = 'Email';
    this.loading = false;
    this.newCompany = emptyNewCompany();
    this.contacts = [];
    this.selectedContactIds = new Set();
    this.includeBroker = false;
  }

  openDialog() {
    this.isEditing = !!this.selectedCompany;
    this.titleFormSnapshot = null;
    this.communicateVia = 'Email';
    this.onFieldChange('communicateVia', 'Email');
    if (this.selectedCompany) {
      const c = this.selectedCompany;
      // When selectedCompany was set via loadTitleSelection (existing agency path), it may only
      // have titleAgencyId + name. Look up the full record from the fetched list for address data.
      const full: TitleCompanyItem = (c.titleAgencyId != null
        ? (this.titleCompanies.find(t => t.titleAgencyId === c.titleAgencyId) ?? c)
        : c);
      const streetNumber = full.streetNumber ?? c.streetNumber ?? '';
      const streetName = full.streetName ?? c.streetName ?? '';
      const noMatchGoogleAddress = full.noMatchGoogleAddress ?? c.noMatchGoogleAddress ?? '';
      const city = full.city ?? c.city ?? '';
      const state = full.state ?? c.state ?? '';
      const zipCode = full.zipCode ?? c.zipCode ?? '';
      const addressObj = streetNumber || streetName || city || state
        ? {
            address: full.address ?? c.address ?? [streetNumber, streetName].filter(Boolean).join(' '),
            streetNumber,
            streetName,
            noMatchGoogleAddress,
            city,
            state,
            zipCode,
            zip: zipCode,
          }
        : (full.address ? { address: full.address } : null);
      this.newCompany = {
        titleAgencyId: c.titleAgencyId ?? null,
        name: c.name,
        address: addressObj,
        streetNumber,
        streetName,
        noMatchGoogleAddress,
        city,
        state,
        zipCode,
      };
      this.searchText = c.name;
      this.contacts = full.contacts?.length ? [...full.contacts] : (c.contacts ? [...c.contacts] : []);
      // selectedContactIds persists from prior loadTitleSelection or toggleContact calls
    }
    this.showTitleDialog = true;
    if (this.isEditing) {
      this.takeTitleSnapshot();
    }
  }

  closeDialog() {
    this.showTitleDialog = false;
  }

  takeTitleSnapshot() {
    const nc = this.newCompany;
    this.titleFormSnapshot = {
      titleAgencyId: nc.titleAgencyId,
      name: nc.name,
      communicateVia: this.communicateVia,
      selectedContactIds: JSON.stringify([...this.selectedContactIds].sort((a, b) => a - b)),
      streetNumber: nc.streetNumber ?? '',
      streetName: nc.streetName ?? '',
      city: nc.city ?? '',
      state: nc.state ?? '',
      zipCode: nc.zipCode ?? '',
      includeBroker: this.includeBroker,
    };
  }

  get hasChanged(): boolean {
    if (!this.isEditing || !this.titleFormSnapshot) return true;
    const snap = this.titleFormSnapshot;
    const nc = this.newCompany;

    const companyChanged =
      nc.titleAgencyId !== snap.titleAgencyId ||
      nc.name !== snap.name;

    const contactsChanged =
      JSON.stringify([...this.selectedContactIds].sort((a, b) => a - b)) !== snap.selectedContactIds;

    const addressChanged =
      (nc.streetNumber ?? '') !== snap.streetNumber ||
      (nc.streetName ?? '') !== snap.streetName ||
      (nc.city ?? '') !== snap.city ||
      (nc.state ?? '') !== snap.state ||
      (nc.zipCode ?? '') !== snap.zipCode;

    const includeBrokerChanged = this.includeBroker !== snap.includeBroker;

    return companyChanged || contactsChanged || addressChanged || includeBrokerChanged;
  }

  setSearchText(text: string) {
    const next = text ?? '';
    if (this.newCompany.titleAgencyId !== null && next !== this.newCompany.name) {
      this.newCompany = emptyNewCompany();
      this.contacts = [];
      this.selectedContactIds = new Set();
    }
    this.newCompany = { ...this.newCompany, name: next };
    this.searchText = next;
  }

  selectExistingCompany(company: TitleCompanyItem) {
    this.newCompany = {
      titleAgencyId: company.titleAgencyId,
      name: company.name,
      address: company.address ?? null,
      streetNumber: company.streetNumber ?? '',
      streetName: company.streetName ?? '',
      noMatchGoogleAddress: company.noMatchGoogleAddress ?? '',
      city: company.city ?? '',
      state: company.state ?? '',
      zipCode: company.zipCode ?? '',
    };
    this.searchText = company.name;

    const rawContacts = (company as any).contacts;
    if (Array.isArray(rawContacts) && rawContacts.length > 0) {
      this.contacts = rawContacts.map((c: any, i: number) => ({
        contactId: c.contactId ?? -(i + 1),
        fullName: c.fullName ?? [c.firstName, c.lastName].filter(Boolean).join(' '),
        phone: c.phone ?? c.cellPhone ?? '',
        email: c.email ?? c.emailAddress ?? '',
      }));
    } else if (company.phone || company.email) {
      this.contacts = [{ contactId: -1, fullName: '', phone: company.phone ?? '', email: company.email ?? '' }];
    } else {
      this.contacts = [];
    }
    // Clear selection — user must explicitly choose contacts for this fresh pick.
    this.selectedContactIds = new Set();
  }

  prefillNewName(name: string) {
    this.newCompany = { ...emptyNewCompany(), name: name ?? '' };
    this.searchText = name ?? '';
    this.contacts = [];
    this.selectedContactIds = new Set();
  }

  changeCompany() {
    if (this.isEditing && this.selectedCompany && this.titleFormSnapshot) {
      const c = this.selectedCompany;
      const snap = this.titleFormSnapshot;
      const full = c.titleAgencyId != null
        ? (this.titleCompanies.find(t => t.titleAgencyId === c.titleAgencyId) ?? c)
        : c;
      const streetNumber = full.streetNumber ?? c.streetNumber ?? '';
      const streetName = full.streetName ?? c.streetName ?? '';
      const noMatchGoogleAddress = (full as any).noMatchGoogleAddress ?? (c as any).noMatchGoogleAddress ?? '';
      const city = full.city ?? c.city ?? '';
      const state = full.state ?? c.state ?? '';
      const zipCode = full.zipCode ?? c.zipCode ?? '';
      const addressObj = streetNumber || streetName || city || state
        ? { address: full.address ?? c.address ?? [streetNumber, streetName].filter(Boolean).join(' '), streetNumber, streetName, noMatchGoogleAddress, city, state, zipCode, zip: zipCode }
        : (full.address ? { address: full.address } : null);
      this.newCompany = { titleAgencyId: c.titleAgencyId ?? null, name: c.name, address: addressObj, streetNumber, streetName, noMatchGoogleAddress, city, state, zipCode };
      this.searchText = c.name;
      this.contacts = full.contacts?.length ? [...full.contacts] : (c.contacts ? [...c.contacts] : []);
      this.communicateVia = 'Email';
      this.selectedContactIds = new Set(JSON.parse(snap.selectedContactIds));
      this.includeBroker = snap.includeBroker;
      return;
    }
    this.selectedCompany = null;
    this.newCompany = emptyNewCompany();
    this.searchText = '';
    this.contacts = [];
    this.selectedContactIds = new Set();
  }

  setCommunicateVia(value: string) {
    this.communicateVia = value;
  }

  setIncludeBroker(value: boolean) {
    this.includeBroker = value;
  }

  setNewCompanyField(field: keyof ReturnType<typeof emptyNewCompany>, value: any) {
    (this.newCompany as any)[field] = value;
  }

  setAddressField(value: any) {
    this.newCompany = {
      ...this.newCompany,
      address: value,
      streetNumber: value?.streetNumber ?? '',
      streetName: value?.streetName ?? '',
      city: value?.city ?? '',
      state: value?.state ?? '',
      zipCode: value?.zipCode ?? value?.zip ?? '',
    };
  }

  addContact(contact: TitleContact) {
    const tempId = -(this.contacts.length + 1);
    const withTempId: TitleContact = { ...contact, contactId: tempId };
    this.contacts = [...this.contacts, withTempId];
    this.selectedContactIds = new Set([...this.selectedContactIds, tempId]);
  }

  updateContact(contactId: number, updated: TitleContact) {
    this.contacts = this.contacts.map(c =>
      c.contactId === contactId ? { ...c, ...updated, contactId } : c
    );
  }

  removeContact(index: number) {
    this.contacts = this.contacts.filter((_, i) => i !== index);
  }

  toggleContact(contactId: number) {
    const next = new Set(this.selectedContactIds);
    if (next.has(contactId)) {
      next.delete(contactId);
    } else {
      next.add(contactId);
    }
    this.selectedContactIds = next;
  }

  saveTitle(pendingDraft?: TitleContact) {
    const { name } = this.newCompany;
    if (!name.trim()) return;
    if (pendingDraft?.email?.trim() && this.contacts.length === 0) {
      this.contacts = [{ ...pendingDraft }];
    }
    const nc = this.newCompany;
    const firstContact = this.contacts[0];
    this.selectedCompany = {
      titleAgencyId: nc.titleAgencyId,
      companyId: null,
      name: nc.name.trim(),
      phone: firstContact?.phone ?? '',
      email: firstContact?.email ?? '',
      address: (typeof nc.address === 'object' ? nc.address?.address : nc.address)
        ?? [nc.streetNumber, nc.streetName].filter(Boolean).join(' '),
      streetNumber: nc.streetNumber,
      streetName: nc.streetName,
      city: nc.city,
      state: nc.state,
      zipCode: nc.zipCode,
      contacts: [...this.contacts],
    };
    this.showTitleDialog = false;
  }

  loadTitleSelection(closingData: any) {
    const { selectedTitleAgencyId, preferredTitle, otherTitleInfo } = closingData ?? {};
    if (selectedTitleAgencyId && preferredTitle && preferredTitle !== 'Other' && preferredTitle !== 'None') {
      this.selectedCompany = { titleAgencyId: selectedTitleAgencyId, companyId: null, name: preferredTitle, contacts: [] };
      this.selectedContactIds = new Set(otherTitleInfo?.selectedTitleContactIds ?? []);
      this.communicateVia = 'Email';
      this.includeBroker = !!otherTitleInfo?.includeBroker;
    } else if (otherTitleInfo?.name) {
      const o = otherTitleInfo;
      const savedContacts: TitleContact[] = Array.isArray(o.contacts)
        ? o.contacts.map((c: any) => ({
            contactId: c.contactId,
            fullName: c.fullName ?? '',
            phone: c.phone ?? c.cellPhone ?? '',
            email: c.email ?? c.emailAddress ?? '',
          }))
        : [];
      const legacyContact: TitleContact = {
        fullName: o.contactName ?? '',
        phone: o.cellPhone ?? '',
        email: o.emailAddress ?? '',
      };
      const contacts = savedContacts.length > 0
        ? savedContacts
        : (legacyContact.fullName || legacyContact.email ? [legacyContact] : []);
      const rawAddr = o.address;
      const addrObj = typeof rawAddr === 'object' && rawAddr !== null ? rawAddr as any : null;
      this.selectedCompany = {
        titleAgencyId: null,
        companyId: null,
        name: o.name ?? '',
        phone: o.cellPhone ?? '',
        email: o.emailAddress ?? '',
        address: addrObj ? addrObj.address ?? '' : rawAddr ?? '',
        streetName: addrObj?.streetName ?? o.streetName ?? '',
        streetNumber: addrObj?.streetNumber ?? o.streetNumber ?? '',
        city: addrObj?.city ?? o.city ?? '',
        state: addrObj?.state ?? o.state ?? '',
        zipCode: addrObj?.zip ?? addrObj?.zipCode ?? o.zipCode ?? '',
        contacts,
      };
      this.communicateVia = 'Email';
      this.selectedContactIds = new Set(o.selectedTitleContactIds ?? []);
      this.includeBroker = !!o.includeBroker;
    }
  }

  getTitleFormValues() {
    if (!this.selectedCompany) return this.getFormValues();
    const c = this.selectedCompany;
    if (c.titleAgencyId !== null) return {};
    return this.getOtherTitleData();
  }

  getOtherTitleData() {
    const c = this.selectedCompany;
    if (!c) return {};
    const isExisting = c.titleAgencyId !== null;
    const firstContact = c.contacts?.[0];
    const allContacts = c.contacts ?? [];
    return {
      ...(isExisting ? {} : {
        name: c.name,
        address: {
          address: typeof c.address === 'object' && c.address !== null
            ? (c.address as any).address ?? ''
            : c.address ?? '',
          streetNumber: c.streetNumber ?? '',
          streetName: c.streetName ?? '',
          city: c.city ?? '',
          state: c.state ?? '',
          zip: c.zipCode ?? '',
        },
      }),
      contactName: firstContact?.fullName ?? '',
      cellPhone: firstContact?.phone ?? '',
      emailAddress: firstContact?.email ?? '',
      communicateVia: 'Email',
      contacts: allContacts.map(ct => ({
        ...(ct.contactId != null && ct.contactId > 0 ? { contactId: ct.contactId } : {}),
        fullName: ct.fullName,
        phone: ct.phone ?? '',
        email: ct.email ?? '',
      })),
      selectedTitleContactIds: Array.from(this.selectedContactIds).filter(id => id > 0),
      includeBroker: this.includeBroker,
    };
  }

  *fetchTitleCompanies(lenderId: number) {
    this.loading = true;
    try {
      const response = yield this.oneToolService.getTitleAgenciesByLenderId(lenderId);
      const raw = response?.data?.data;
      const list = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : [];
      this.titleCompanies = Array.isArray(list) ? list : [];
    } catch (e) {
      this.titleCompanies = [];
    } finally {
      this.loading = false;
    }
  }
}
